import { useState, useEffect } from "react";
import { toast } from "react-toastify";

import toastError from "../../errors/toastError";
import {
  findQueueKeywords,
  createQueueKeyword,
  updateQueueKeyword,
  deleteQueueKeyword,
} from "../../services/queue.api";

// D8.1.4a — CRUD de grupos de palabras clave del modal de colas.
// IN: open, queueId, selectedPrompt (dispara la carga). Estado y handlers propios.
export default function useKeywordGroups({ open, queueId, selectedPrompt }) {
  const [keywordGroups, setKeywordGroups] = useState([]);
  const [keywordsEnabled, setKeywordsEnabled] = useState(false);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showGroupForm, setShowGroupForm] = useState(false);

  // Cargar grupos de keywords cuando se abre el modal y hay queueId con IA
  useEffect(() => {
    const loadKeywordGroups = async () => {
      if (!open || !queueId || !selectedPrompt) {
        setKeywordGroups([]);
        setKeywordsEnabled(false);
        return;
      }

      setLoadingKeywords(true);
      try {
        const { data } = await findQueueKeywords(queueId);
        setKeywordGroups(data || []);
        if (data && data.length > 0) {
          setKeywordsEnabled(true);
        }
      } catch (err) {
        // Si no hay grupos, no es error (puede estar vacío)
        if (err.response?.status !== 404) {
          toastError(err);
        }
        setKeywordGroups([]);
      } finally {
        setLoadingKeywords(false);
      }
    };

    loadKeywordGroups();
  }, [open, queueId, selectedPrompt]);

  const handleCreateKeywordGroup = async (groupData) => {
    // Validar que queueId sea válido antes de la petición
    if (!queueId || isNaN(Number(queueId)) || Number(queueId) <= 0) {
      toast.error("Error: No se ha seleccionado un departamento válido");
      return;
    }

    try {
      const { data } = await createQueueKeyword(queueId, groupData);
      setKeywordGroups([...keywordGroups, data]);
      setShowGroupForm(false);
      setEditingGroup(null);
      toast.success("Grupo de keywords creado correctamente");
    } catch (err) {
      toastError(err);
    }
  };

  const handleUpdateKeywordGroup = async (groupId, groupData) => {
    try {
      const { data } = await updateQueueKeyword(queueId, groupId, groupData);
      setKeywordGroups(keywordGroups.map(g => g.id === groupId ? data : g));
      setShowGroupForm(false);
      setEditingGroup(null);
      toast.success("Grupo de keywords actualizado correctamente");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteKeywordGroup = async (groupId) => {
    if (!window.confirm("¿Estás seguro de eliminar este grupo de keywords?")) {
      return;
    }
    try {
      await deleteQueueKeyword(queueId, groupId);
      setKeywordGroups(keywordGroups.filter(g => g.id !== groupId));
      toast.success("Grupo de keywords eliminado correctamente");
    } catch (err) {
      toastError(err);
    }
  };

  return {
    keywordGroups,
    setKeywordGroups,
    keywordsEnabled,
    setKeywordsEnabled,
    loadingKeywords,
    editingGroup,
    setEditingGroup,
    showGroupForm,
    setShowGroupForm,
    handleCreateKeywordGroup,
    handleUpdateKeywordGroup,
    handleDeleteKeywordGroup,
  };
}
